/**
 * Zustand store for analysis feature
 */

import { create } from 'zustand';
import type {
  AnalysisInput,
  AnalysisProgress,
  AnalysisResult,
  AnalysisError,
} from '@/types/analysis';
import type { Chord, MusicalKey, Mode } from '@/types/chord';
import type { PhraseBoundary } from '@/types/progression';
import type { DeconstructionStep } from './build-from-bones-store';
import type { ExtractSlicesResponse } from '@/services/api-service';
import { convertAnalysisResultToChords, quantizeProgression } from '@/utils/chord-converter';
import { useCanvasStore } from './canvas-store';
import { analyzeScore, analyzeScoreParts, analyzeCorpusWork, extractVerticalSlices, getAuthHeaders } from '@/services/api-service';

interface AnalysisState {
  // Modal state
  isModalOpen: boolean;
  activeTab: 'youtube' | 'audio';
  showResultsView: boolean;

  // Input state
  input: AnalysisInput | null;

  // Process state
  isAnalyzing: boolean;
  progress: AnalysisProgress | null;
  error: AnalysisError | null;
  result: AnalysisResult | null;

  // Metadata for banner display
  metadata: {
    title: string;
    composer?: string;
    sourceUrl?: string;
    analyzedAt: string;
    /**
     * Version of the backend analyzer behind this result (81ffbb11).
     * Undefined on the browser-side audio/YouTube path, which has none.
     */
    analyzerVersion?: string;
    /**
     * Which analyze entry point produced this result, so a saved
     * progression's analyzedFrom.source records the truth instead of being
     * hardcoded to 'youtube' for every path (import/library included).
     */
    source: 'youtube' | 'audio' | 'file' | 'library';
  } | null;

  // Converted chords ready for canvas
  convertedChords: Chord[] | null;

  // Phrase boundaries for visual grouping
  phraseBoundaries: PhraseBoundary[] | null;

  // Deconstruction state
  deconstructionSteps: DeconstructionStep[] | null;
  isDeconstructing: boolean;
  deconstructionError: AnalysisError | null;

  // Renaissance Analysis state
  verticalSlices: ExtractSlicesResponse | null;
  isExtractingSlices: boolean;
  slicesError: AnalysisError | null;
  musicxmlContent: string | null;

  // Actions
  openModal: () => void;
  closeModal: () => void;
  setActiveTab: (tab: 'youtube' | 'audio') => void;
  setInput: (input: AnalysisInput) => void;
  clearInput: () => void;
  startAnalysis: (input: AnalysisInput) => Promise<void>;
  startDeconstruction: () => Promise<void>;
  extractRenaissanceAnalysis: () => Promise<void>;
  updateProgress: (progress: AnalysisProgress) => void;
  setError: (error: AnalysisError) => void;
  setResult: (result: AnalysisResult) => void;
  cancelAnalysis: () => void;
  clearAnalyzedProgression: () => void;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const useAnalysisStore = create<AnalysisState>((set, get) => ({
  // Initial state
  isModalOpen: false,
  activeTab: 'youtube',
  showResultsView: false,
  input: null,
  isAnalyzing: false,
  progress: null,
  error: null,
  result: null,
  metadata: null,
  convertedChords: null,
  phraseBoundaries: null,
  deconstructionSteps: null,
  isDeconstructing: false,
  deconstructionError: null,
  verticalSlices: null,
  isExtractingSlices: false,
  slicesError: null,
  musicxmlContent: null,

  // Modal actions
  openModal: () => set({ isModalOpen: true, error: null, result: null, progress: null, showResultsView: false }),
  closeModal: () => {
    const { isAnalyzing, isDeconstructing } = get();
    if (!isAnalyzing && !isDeconstructing) {
      set({ isModalOpen: false, showResultsView: false });
    }
  },
  setActiveTab: (tab) => set({ activeTab: tab, error: null }),

  // Input actions
  setInput: (input) => set({ input, error: null }),
  clearInput: () => set({ input: null, error: null }),

  // Analysis actions - implement full API call logic or client-side file parsing
  startAnalysis: async (input) => {
    set({
      isAnalyzing: true,
      input,
      error: null,
      progress: { stage: 'uploading', progress: 0, message: 'Preparing...' },
    });

    try {
      // Handle file type inputs
      if (input.type === 'file') {
        let analysisResult: AnalysisResult;
        let musicxmlData = '';
        // keySegments from backend windowed detection (only populated on single-file path)
        let detectedKeySegments: import('@/types/progression').KeySegment[] = [];

        // Library import — analyze a built-in corpus work by id, no upload needed.
        if (input.corpusWork) {
          // One request covers load + chordify on the backend, so one message.
          // (Two set() calls in the same tick would render only the second.)
          set({
            progress: { stage: 'analyzing', progress: 50, message: `Reading ${input.corpusWork.title}...` },
          });

          const scoreResp = await analyzeCorpusWork(input.corpusWork.id, {
            keyHint: input.keyHint && input.keyHint !== 'auto' ? input.keyHint : undefined,
            modeHint: input.modeHint && input.modeHint !== 'auto' ? input.modeHint : undefined,
          });

          if (!scoreResp.chords || scoreResp.chords.length === 0) {
            throw new Error('No chords detected. This work may be a single melodic line.');
          }

          // Capture key segments from the backend response, same as the single-file path.
          detectedKeySegments = scoreResp.keySegments ?? [];

          analysisResult = {
            title: scoreResp.title || input.corpusWork.title,
            composer: scoreResp.composer || input.corpusWork.composer,
            key: scoreResp.key || 'C',
            mode: (scoreResp.mode as 'major' | 'minor') || 'major',
            tempo: scoreResp.tempo || 120,
            timeSignature: scoreResp.timeSignature || '4/4',
            chords: scoreResp.chords,
            analyzedAt: new Date().toISOString(),
            analyzerVersion: scoreResp.analyzerVersion,
          };
        } else if (input.voiceAssignments && input.voiceAssignments.length > 0) {
          // Multi-file SATB merge — backend music21 stream-merge path (Phase 3b).
          set({
            progress: { stage: 'analyzing', progress: 40, message: 'Merging voice parts...' },
          });

          const scoreResp = await analyzeScoreParts(
            input.voiceAssignments.map((a) => ({
              file: a.file,
              voice: a.voiceId,
              label: a.file.name,
            })),
            {
              keyHint: input.keyHint && input.keyHint !== 'auto' ? input.keyHint : undefined,
              modeHint: input.modeHint && input.modeHint !== 'auto' ? input.modeHint : undefined,
            }
          );

          if (!scoreResp.chords || scoreResp.chords.length === 0) {
            throw new Error('No chords detected. These files may be single melodic lines.');
          }

          // Multi-file merge uses browser-side detection — no backend keySegments
          detectedKeySegments = [];

          analysisResult = {
            title: 'Imported Progression',
            composer: scoreResp.composer,
            key: scoreResp.key || 'C',
            mode: (scoreResp.mode as 'major' | 'minor') || 'major',
            tempo: scoreResp.tempo || 120,
            timeSignature: scoreResp.timeSignature || '4/4',
            chords: scoreResp.chords,
            analyzedAt: new Date().toISOString(),
            analyzerVersion: scoreResp.analyzerVersion,
          };
        } else {
          // Single-file path — routes through the music21 backend.
          if (!input.importFile) {
            throw new Error('No file provided');
          }
          if (!input.importFormat) {
            throw new Error('No file format specified');
          }

          set({
            progress: { stage: 'processing', progress: 25, message: 'Reading file...' },
          });

          let scoreFile: File | Blob = input.importFile;
          let scoreFormat: 'midi' | 'musicxml';

          // PDF: round-trip through oemer first to get MusicXML, then analyze.
          if (input.importFormat === 'pdf') {
            set({
              progress: { stage: 'processing', progress: 10, message: 'Reading the score (30-90 seconds)...' },
            });

            const formData = new FormData();
            formData.append('file', input.importFile);

            const authHeaders = await getAuthHeaders();
            const pdfHeaders: Record<string, string> = {};
            const authValue = (authHeaders as Record<string, string>)['Authorization'];
            if (authValue) pdfHeaders['Authorization'] = authValue;

            const response = await fetch(`${API_BASE_URL}/api/analyze/pdf`, {
              method: 'POST',
              headers: pdfHeaders,
              body: formData,
            });

            if (response.status === 401) {
              throw new Error('Sign in to import PDFs. (PDF conversion is auth-gated.)');
            }

            const data = await response.json();
            if (!data.success) {
              throw new Error(data.error || 'PDF analysis failed');
            }

            // Re-pack the OMR-produced MusicXML as a Blob for the analyzer.
            const musicxmlBytes = Uint8Array.from(atob(data.musicxml), c => c.charCodeAt(0));
            scoreFile = new Blob([musicxmlBytes], { type: 'application/xml' });
            scoreFormat = 'musicxml';
            // Keep the MusicXML around for the Renaissance / extract-slices path.
            musicxmlData = new TextDecoder().decode(musicxmlBytes);
          } else if (input.importFormat === 'midi') {
            scoreFormat = 'midi';
          } else if (input.importFormat === 'musicxml') {
            scoreFormat = 'musicxml';
            musicxmlData = await input.importFile.text();
          } else {
            throw new Error(`Unsupported format: ${input.importFormat}`);
          }

          // Backend music21 pipeline: chordify + romanNumeralFromChord + key analysis.
          set({
            progress: { stage: 'analyzing', progress: 60, message: 'Detecting chords...' },
          });

          const scoreResp = await analyzeScore(scoreFile, scoreFormat, {
            keyHint: input.keyHint && input.keyHint !== 'auto' ? input.keyHint : undefined,
            modeHint: input.modeHint && input.modeHint !== 'auto' ? input.modeHint : undefined,
          });

          if (!scoreResp.chords || scoreResp.chords.length === 0) {
            throw new Error('No chords detected. This file may be a single melodic line.');
          }

          // Capture key segments from single-file backend response
          detectedKeySegments = scoreResp.keySegments ?? [];

          analysisResult = {
            title: scoreResp.title
              || (input.importFile?.name ? input.importFile.name.replace(/\.[^.]+$/, '') : 'Imported Progression'),
            composer: scoreResp.composer,
            key: scoreResp.key || 'C',
            mode: (scoreResp.mode as 'major' | 'minor') || 'major',
            tempo: scoreResp.tempo || 120,
            timeSignature: scoreResp.timeSignature || '4/4',
            chords: scoreResp.chords,
            analyzedAt: new Date().toISOString(),
            analyzerVersion: scoreResp.analyzerVersion,
          };
        }

        // Step 5: Convert to canvas format
        const rawChords = convertAnalysisResultToChords(analysisResult);

        // Quantize the progression for clearer harmonic analysis:
        // - Removes consecutive duplicate chords
        // - Detects repeating patterns (2-6 chords) with fuzzy matching
        // - Keeps 2 cycles of each unique pattern (verse, chorus, etc.)
        // - Normalizes all chord durations to 2 beats each
        const { chords: convertedChords, phrases: phraseBoundaries } = quantizeProgression(rawChords, 2);

        // Create metadata for banner display
        // Library import sets input.corpusWork; every other branch under
        // input.type === 'file' (single-file, multi-file SATB merge) is a
        // user-supplied file.
        const source: 'library' | 'file' = input.corpusWork ? 'library' : 'file';
        const metadata = {
          title: analysisResult.title,
          composer: analysisResult.composer,
          analyzedAt: analysisResult.analyzedAt,
          analyzerVersion: analysisResult.analyzerVersion,
          source,
        };

        // Apply to canvas store - update key, mode, tempo, chords, phrases, and key segments
        useCanvasStore.getState().loadAnalyzedProgression({
          chords: convertedChords,
          phrases: phraseBoundaries,
          keySegments: detectedKeySegments,
          key: analysisResult.key as MusicalKey,
          mode: analysisResult.mode as Mode,
          tempo: analysisResult.tempo,
        });

        set({
          result: analysisResult,
          convertedChords,
          phraseBoundaries,
          metadata,
          musicxmlContent: musicxmlData,
          isAnalyzing: false,
          progress: { stage: 'complete', progress: 100, message: 'Analysis complete!' },
          showResultsView: true,
          deconstructionSteps: null,
          deconstructionError: null,
        });

        return;
      }

      // Handle audio/youtube uploads (legacy backend API)
      // If audio file, upload first
      if (input.type === 'audio' && input.audioFile) {
        set({
          progress: {
            stage: 'uploading',
            progress: 25,
            message: 'Uploading audio file...',
          },
        });

        const formData = new FormData();
        formData.append('file', input.audioFile);

        const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadRes.ok) throw new Error('Upload failed');
        const { uploadId } = await uploadRes.json();
        input.uploadId = uploadId;
      }

      set({
        progress: {
          stage: 'analyzing',
          progress: 50,
          message: 'Analyzing chords...',
        },
      });

      const response = await fetch(`${API_BASE_URL}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: input.type,
          youtubeUrl: input.youtubeUrl,
          videoId: input.videoId,
          uploadId: input.uploadId,
          startTime: input.startTime,
          endTime: input.endTime,
          keyHint: input.keyHint || 'auto',
          modeHint: input.modeHint || 'auto',
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Convert the analyzed chords to canvas format
        const rawChords = convertAnalysisResultToChords(data.result);

        // Quantize the progression for clearer harmonic analysis:
        // - Removes consecutive duplicate chords
        // - Detects repeating patterns (2-6 chords) with fuzzy matching
        // - Keeps 2 cycles of each unique pattern (verse, chorus, etc.)
        // - Normalizes all chord durations to 2 beats each
        const { chords: convertedChords, phrases: phraseBoundaries } = quantizeProgression(rawChords, 2);

        // Create metadata for banner display
        const legacySource: 'audio' | 'youtube' = input.type === 'audio' ? 'audio' : 'youtube';
        const metadata = {
          title: data.result.title || (input.type === 'youtube' && input.videoId ? `YouTube: ${input.videoId}` : 'Analyzed Progression'),
          sourceUrl: input.youtubeUrl,
          analyzedAt: new Date().toISOString(),
          source: legacySource,
        };

        // Apply to canvas store - update key, mode, tempo, chords, phrases, and key segments
        // (audio/youtube path uses browser-side detection — no backend keySegments)
        useCanvasStore.getState().loadAnalyzedProgression({
          chords: convertedChords,
          phrases: phraseBoundaries,
          keySegments: [],
          key: data.result.key as MusicalKey,
          mode: data.result.mode as Mode,
          tempo: data.result.tempo,
        });

        set({
          result: data.result,
          convertedChords,
          phraseBoundaries,
          metadata,
          isAnalyzing: false,
          progress: { stage: 'complete', progress: 100, message: 'Analysis complete!' },
          showResultsView: true, // Show results in modal instead of auto-closing
          deconstructionSteps: null, // Reset deconstruction when new analysis is done
          deconstructionError: null,
        });
      } else {
        set({
          error: data.error,
          isAnalyzing: false,
          progress: null,
        });
      }
    } catch (error) {
      set({
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
          retryable: true,
        },
        isAnalyzing: false,
        progress: null,
      });
    }
  },

  // Deconstruction action
  startDeconstruction: async () => {
    const { result, convertedChords } = get();

    if (!result || !convertedChords) {
      set({
        deconstructionError: {
          code: 'ANALYSIS_FAILED',
          message: 'Please analyze a progression first',
          retryable: false,
        },
      });
      return;
    }

    set({
      isDeconstructing: true,
      deconstructionError: null,
    });

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/deconstruct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          chords: convertedChords.map((chord) => ({
            quality: chord.quality,
            scaleDegree: chord.scaleDegree,
          })),
          key: result.key,
          mode: result.mode,
        }),
      });

      const data = await response.json();

      if (data.success && data.steps) {
        set({
          deconstructionSteps: data.steps,
          isDeconstructing: false,
          deconstructionError: null,
        });
      } else {
        set({
          deconstructionError: data.error || {
            code: 'ANALYSIS_FAILED',
            message: 'Failed to deconstruct progression',
            retryable: true,
          },
          isDeconstructing: false,
        });
      }
    } catch (error) {
      set({
        deconstructionError: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
          retryable: true,
        },
        isDeconstructing: false,
      });
    }
  },

  // Renaissance Analysis action
  extractRenaissanceAnalysis: async () => {
    const { musicxmlContent, result } = get();

    if (!musicxmlContent) {
      set({
        slicesError: {
          code: 'MISSING_DATA',
          message: 'MusicXML data not available. Please analyze a score file first.',
          retryable: false,
        },
      });
      return;
    }

    set({
      isExtractingSlices: true,
      slicesError: null,
    });

    try {
      const response = await extractVerticalSlices(
        musicxmlContent,
        result?.key,
        true
      );

      if (response.success && response.reduction) {
        set({
          verticalSlices: response,
          isExtractingSlices: false,
          slicesError: null,
        });
      } else {
        set({
          slicesError: response.error ? {
            code: 'ANALYSIS_FAILED',
            message: response.error,
            retryable: true,
          } : {
            code: 'ANALYSIS_FAILED',
            message: 'Failed to extract vertical slices',
            retryable: true,
          },
          isExtractingSlices: false,
        });
      }
    } catch (error) {
      set({
        slicesError: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
          retryable: true,
        },
        isExtractingSlices: false,
      });
    }
  },

  updateProgress: (progress) => set({ progress }),
  setError: (error) => set({ error, isAnalyzing: false, progress: null }),
  setResult: (result) => set({ result, isAnalyzing: false }),
  cancelAnalysis: () => set({ isAnalyzing: false, progress: null }),
  clearAnalyzedProgression: () =>
    set({
      result: null,
      metadata: null,
      convertedChords: null,
      phraseBoundaries: null,
      deconstructionSteps: null,
      deconstructionError: null,
      verticalSlices: null,
      slicesError: null,
      musicxmlContent: null,
    }),
  reset: () =>
    set({
      input: null,
      error: null,
      progress: null,
      result: null,
      metadata: null,
      convertedChords: null,
      phraseBoundaries: null,
      deconstructionSteps: null,
      deconstructionError: null,
      verticalSlices: null,
      slicesError: null,
      musicxmlContent: null,
      showResultsView: false,
      activeTab: 'youtube',
    }),
}));
